import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
// Import Shared Components
import {
  Loader,
  Table,
  DatePicker,
  Dropdown,
  Modal,
  ActionBar,
  ButtonGroup,
} from 'shared-components';
// Import Shared Utils
import { get, put } from 'shared-components/src/utils/http';
import {
  getEndOfDay,
  subtractMonth,
  currentDateTime,
  getStartAndEndDates,
} from 'shared-components/src/utils/dates';
import { formatDateForTransactionQuery } from 'shared-components/src/utils/formatDateTime';
import { formatCurrencyV } from 'shared-components/src/utils/formatCurrency';
import { centsToDollars } from 'shared-components/src/utils/currency';
import {
  getFormattedTransferType,
  getFormattedStatus,
} from 'shared-components/src/utils/cardholders';
// Import Global Components
import Heading from '../../../../../components/Heading';
import Alert from '../../../../../components/Alert';
import Label from '../../../../../components/Label';
import Button from '../../../../../components/Button';
import CancelButton from '../../../../../components/CancelButton';
import Text from '../../../../../components/Text';
// Import Global Layouts
import { ModalFormContainer } from '../../../../../layouts/Form';
// Import Component Styles
import { StatusBadge } from '../styles';
import { FilterContainer } from '../../ExternalAccounts/styles';
import { TransactionsHeader, DatesContainer } from '../../../../Home/styles';

const TransfersCard = ({ page }) => {
  const intl = useIntl();

  // Transfers states
  const [transfers, setTransfers] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  // Modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTransferId, setCancelTransferId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  // Date filter states
  const [startDate, setStartDate] = useState(
    subtractMonth(currentDateTime(), 1)
  );
  const [endDate, setEndDate] = useState(getEndOfDay());
  const [focusedInput, setFocusedInput] = useState(null);

  // Type filter state
  const [selectedType, setSelectedType] = useState('');

  // Type filter options
  const typeFilterOptions = [
    { key: '', val: intl.messages[`${page}-type-all`] || 'All Types' },
    {
      key: 'direct_send_etransfer_push',
      val: intl.messages[`${page}-type-push`] || 'E-Transfer Push',
    },
    {
      key: 'direct_send_etransfer_pull',
      val: intl.messages[`${page}-type-pull`] || 'E-Transfer Pull',
    },
    {
      key: 'direct_send_transaction',
      val: intl.messages[`${page}-type-direct-send`] || 'Direct Send Transfer',
    },
    {
      key: 'direct_send_pull_transaction',
      val:
        intl.messages[`${page}-type-direct-send-pull`] ||
        'Direct Send Pull Transfer',
    },
  ];

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageSize = 10;

  const createIntl = key =>
    intl.formatMessage({
      id: `date-${key}-label`,
      defaultMessage: intl.messages[`date-${key}-label`],
      description: `Date ${key} label`,
    });

  // Fetch transfers
  const fetchTransfers = async (pageNum = 1) => {
    setTransfersLoading(true);
    try {
      const offset = (pageNum - 1) * pageSize;
      const params = new URLSearchParams({
        transfer_type: selectedType,
        start_date: formatDateForTransactionQuery(startDate),
        end_date: formatDateForTransactionQuery(endDate),
        offset: offset.toString(),
        limit: pageSize.toString(),
      });

      // Add transfer_type filter if selected
      if (selectedType) {
        params.append('transfer_type', selectedType);
      }

      const transfersResponse = await get(
        `/api/v1/cardholder_portal/cardholder/transfers?${params.toString()}`
      );

      const responseData = transfersResponse.data || [];
      setTransfers(responseData);
      // If data count equals page size, there might be more pages
      setHasNextPage(responseData.length === pageSize);
      setCurrentPage(pageNum);
    } catch (e) {
      setTransfers([]);
      setHasNextPage(false);
      setCurrentPage(1);
    }
    setTransfersLoading(false);
  };

  // Handle page change
  const handlePageChange = pageNum => {
    fetchTransfers(pageNum);
  };

  // Fetch transfers when filters change
  useEffect(() => {
    fetchTransfers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedType]);

  // Handle cancel transfer
  const handleCancelTransfer = async () => {
    setCancelling(true);
    try {
      await put(
        `/api/v2/cardholder_portal/cardholder/external_transfers/${cancelTransferId}/cancel`
      );

      // Update the transfer status in the list
      setTransfers(prev =>
        prev.map(transfer =>
          transfer.id === cancelTransferId
            ? { ...transfer, status: 'cancelled' }
            : transfer
        )
      );

      setAlert({
        type: 'success',
        message:
          intl.messages[`${page}-cancel-success-alert`] ||
          'Transfer cancelled successfully',
        messageFor: 'cancel-success',
      });

      setShowCancelModal(false);
      setCancelTransferId(null);
    } catch (e) {
      setCancelError(
        intl.messages[`${page}-cancel-error-alert`]
      );
    }
    setCancelling(false);
  };

  // Open cancel modal
  const openCancelModal = (e, transferId) => {
    e.preventDefault();
    setAlert(null);
    setCancelError(null);
    setCancelTransferId(transferId);
    setShowCancelModal(true);
  };

  // Close cancel modal
  const closeCancelModal = e => {
    e.preventDefault();
    setShowCancelModal(false);
    setCancelTransferId(null);
    setCancelError(null);
  };

  /* eslint-disable react/no-unstable-nested-components */
  const transferColumns = [
    {
      Header: () => (
        <FormattedMessage
          id={`${page}-date-table-header`}
          description="Table Header date"
          defaultMessage="Date"
        />
      ),
      accessor: 'created_at',
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => <FormattedDate value={value} />,
      width: 120,
    },
    {
      Header: () => (
        <FormattedMessage
          id={`${page}-name-table-header`}
          description="Table Header name"
          defaultMessage="Name"
        />
      ),
      accessor: 'name',
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => value,
      width: 200,
    },
    {
      Header: () => (
        <FormattedMessage
          id={`${page}-type-table-header`}
          description="Table Header type"
          defaultMessage="Type"
        />
      ),
      accessor: 'transfer_type',
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => getFormattedTransferType(value),
    },
    {
      Header: () => (
        <FormattedMessage
          id={`${page}-status-table-header`}
          description="Table Header status"
          defaultMessage="Status"
        />
      ),
      accessor: 'status',
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => (
        <StatusBadge $status={value}>{getFormattedStatus(value)}</StatusBadge>
      ),
      width: 150,
    },
    {
      Header: () => (
        <FormattedMessage
          id={`${page}-amount-table-header`}
          description="Table Header amount"
          defaultMessage="Amount"
        />
      ),
      accessor: 'amount',
      width: 120,
      // eslint-disable-next-line react/prop-types
      Cell: ({ original }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrencyV(centsToDollars(original?.amount || 0))}
        </div>
      ),
    },
    {
      Header: () => (
        <FormattedMessage
          id={`${page}-action-table-header`}
          description="Table Header action"
          defaultMessage="Action"
        />
      ),
      accessor: 'action',
      width: 120,
      innerFocus: true,
      // eslint-disable-next-line react/prop-types
      Cell: ({ original }) =>
        original?.status === 'awaiting_settlement' && (
          <CancelButton onClick={e => openCancelModal(e, original?.id)} />
        ),
    },
  ];
  /* eslint-enable react/no-unstable-nested-components */

  return (
    <>
      {alert && (
        <Alert
          type={alert.type}
          alert={alert.message}
          alertFor={alert.messageFor}
          page={page}
        />
      )}
      <TransactionsHeader>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <FilterContainer>
            <Label
              labelFor="type-filter"
              page={page}
              label={
                intl.messages[`${page}-type-filter-label`] || 'Filter by type:'
              }
            />
            <Dropdown
              options={typeFilterOptions}
              selected={selectedType}
              updateItem={val => setSelectedType(val)}
            />
          </FilterContainer>
          <DatesContainer>
            <DatePicker
              toPlaceholder="MM/DD/YYYY"
              fromPlaceholder="MM/DD/YYYY"
              startDate={startDate}
              endDate={endDate}
              onDatesChange={({ startDate: newStart, endDate: newEnd }) => {
                const { validStartDate, validEndDate } = getStartAndEndDates(
                  newStart,
                  newEnd
                );
                setStartDate(validStartDate);
                setEndDate(validEndDate);
              }}
              focusedInput={focusedInput}
              onFocusChange={input => {
                setFocusedInput(input);
              }}
              isOutsideRange={() => false}
              showDefaultInputIcon
              toText={createIntl('to')}
              fromText={createIntl('from')}
              label="true"
            />
          </DatesContainer>
        </div>
      </TransactionsHeader>

      {transfersLoading && <Loader />}
      {!transfersLoading && transfers.length === 0 && (
        <Alert
          type="info"
          alert={
            intl.messages[`${page}-no-transfers-alert`] ||
            'No transfers found for this account.'
          }
          alertFor="no-transfers"
          page={page}
        />
      )}
      {!transfersLoading && transfers.length > 0 && (
        <Table
          data={transfers}
          columns={transferColumns}
          showPagination={currentPage > 1 || hasNextPage}
          showPageSizeOptions={false}
          manual
          pages={hasNextPage ? currentPage + 1 : currentPage}
          page={currentPage - 1}
          onPageChange={pageIndex => handlePageChange(pageIndex + 1)}
          pageSize={pageSize}
        />
      )}

      {/* Cancel Transfer Modal */}
      {showCancelModal && (
        <Modal>
          <ModalFormContainer>
            <Heading
              page={`${page}-cancel`}
              heading={
                intl.messages[`${page}-cancel-heading`] || 'Cancel Transfer'
              }
            />
            {cancelError && (
              <Alert
                type="danger"
                alert={cancelError}
                alertFor="cancel-error"
                page={page}
              />
            )}
            {cancelling ? (
              <Loader />
            ) : (
              <>
                <Text
                  text={
                    intl.messages[`${page}-cancel-confirm-text`] ||
                    'Are you sure you want to cancel this transfer? This action cannot be undone.'
                  }
                  textFor="cancel-confirm"
                  page={page}
                />
                <ActionBar>
                  <ButtonGroup reverse>
                    <Button
                      buttonText={
                        intl.messages[`${page}-confirm-cancel-button`] ||
                        'Yes, Cancel Transfer'
                      }
                      buttonFor="confirm-cancel"
                      page={page}
                      onClick={handleCancelTransfer}
                      type="danger"
                    />
                    <Button
                      buttonText={
                        intl.messages[`${page}-keep-button`] ||
                        'No, Keep Transfer'
                      }
                      buttonFor="keep"
                      page={page}
                      onClick={closeCancelModal}
                      type="default"
                    />
                  </ButtonGroup>
                </ActionBar>
              </>
            )}
          </ModalFormContainer>
        </Modal>
      )}
    </>
  );
};

TransfersCard.propTypes = {
  page: PropTypes.string.isRequired,
};

export default TransfersCard;
